'use client';

// Network-aware in-app `<Link>`. Wraps next-intl's locale-aware Link
// with a demo-mode prefix rewriter driven by the unified `useNetworkMode()`
// hook (gate-clamped store value):
//
//   - Mode = `'devnet'` (pre-launch always; post-launch when user toggles
//     to demo): every demo-mode-aware `href` gets `/demo-mode/` prepended.
//   - Mode = `'mainnet'` (post-launch default): no rewriting. Links point
//     at bare paths which serve real mainnet content.
//
// Excepted paths (about/learn/terms/auth/onboarding/...) are never
// prefixed — they have no demo equivalent. See `DEMO_MODE_EXCEPTIONS`.
// Visiting them does NOT change the user's mode (mode is store-driven,
// not URL-driven), so a devnet user clicking "About" stays devnet on
// the SDK and stays devnet when they click back to a market.
import { c as _c } from "react/compiler-runtime";
import type { LinkProps } from 'next/link';
import { createNavigation } from 'next-intl/navigation';
import type { ComponentProps, ForwardedRef } from 'react';
import { addDemoModePath, isDemoModeAwarePath } from '@/lib/network';
import { useNetworkPresentation } from '@/lib/network/presentation-context';
import { useNetworkMode } from '@/lib/network/use-network-mode';
import { holdNotifyUntilRouteCommit } from '@/lib/sdk/route-change-notify-hold';
import { routing } from './routing';
type Navigation = ReturnType<typeof createNavigation>;
const navigation: Navigation = createNavigation(routing);
type IntlLink = Navigation['Link'];
type IntlLinkProps = ComponentProps<IntlLink>;

/** True when a Link should be rewritten to its `/demo-mode/` form. */
function shouldRewriteForDemo(targetPathname: string, mode: 'mainnet' | 'devnet'): boolean {
  if (mode === 'mainnet') return false;
  return isDemoModeAwarePath(targetPathname);
}
function rewriteHref(href: IntlLinkProps['href'], mode: 'mainnet' | 'devnet'): IntlLinkProps['href'] {
  if (typeof href === 'string') {
    if (!shouldRewriteForDemo(href, mode)) return href;
    return addDemoModePath(href);
  }
  // next-intl typed-route object form: { pathname, params, query }. We
  // rewrite `pathname`; `params` (dynamic segments) and `query` are
  // unaffected because the demo-mode prefix is purely a route-tree
  // discriminator, not a parameter.
  if (typeof href === 'object' && href !== null && 'pathname' in href) {
    const pathname = (href as {
      pathname: string;
    }).pathname;
    if (!shouldRewriteForDemo(pathname, mode)) return href;
    return {
      ...(href as object),
      pathname: addDemoModePath(pathname)
    } as IntlLinkProps['href'];
  }
  return href;
}
type InnerLinkProps = IntlLinkProps & {
  ref?: ForwardedRef<HTMLAnchorElement>;
};
// next-intl forwards `onNavigate` to `next/link` at runtime but omits it from its types.
type NextIntlLinkProps = InnerLinkProps & Pick<LinkProps, 'onNavigate'>;
// ALLOW-UNKNOWN: next-intl's typed Link prop union clashes with the post-rewrite href shape; runtime behavior is identical.
const InnerLink: (p: NextIntlLinkProps) => React.ReactNode = navigation.Link as unknown as (p: NextIntlLinkProps) => React.ReactNode;

// React 19: `ref` is a normal prop (no forwardRef). `InnerLinkProps` already
// carries the optional `ref`, so spreading props forwards it to InnerLink.
function NetworkLinkInner(props) {
  const $ = _c(10);
  const mode = useNetworkMode();
  const presentation = useNetworkPresentation();
  let t0;
  if ($[0] !== mode || $[1] !== presentation || $[2] !== props.href) {
    const finalHref = rewriteHref(props.href, mode);
    t0 = presentation !== null && typeof finalHref === "string" && finalHref.startsWith("/") ? new URL(finalHref, presentation.publicSiteOrigin).toString() : finalHref;
    $[0] = mode;
    $[1] = presentation;
    $[2] = props.href;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const href = t0;
  let t1;
  if ($[4] !== href) {
    t1 = function onNavigate() {
      if (changesPathname(href)) {
        holdNotifyUntilRouteCommit();
      }
    };
    $[4] = href;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const onNavigate = t1;
  let t2;
  if ($[6] !== href || $[7] !== onNavigate || $[8] !== props) {
    t2 = <InnerLink {...props} href={href} onNavigate={onNavigate} />;
    $[6] = href;
    $[7] = onNavigate;
    $[8] = props;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}

// Routing uses `localePrefix: 'never'`, so link and location pathnames compare
// directly. Same-pathname (search-only) navigations must not hold: nothing
// would release the hold, and the page itself renders the new query's data.
function changesPathname(href: IntlLinkProps['href']): boolean {
  const pathname = typeof href === 'string' ? new URL(href, window.location.href).pathname : href.pathname;
  return pathname !== window.location.pathname;
}

// ALLOW-UNKNOWN: re-export under the canonical IntlLink type so the public surface matches next-intl's Link 1:1.
export const NetworkLink: IntlLink = NetworkLinkInner as unknown as IntlLink;